import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileXls,
  FileDoc,
  ArrowClockwise,
  Download,
  CheckCircle,
  Warning,
  Clock,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

// Vincent QA Batches admin — Eric 2026-07-27 (iter159)
// Affiche l'historique des XLSX de consignes de Vincent traités par la
// toolchain (parse_vincent_xlsx.py → gen_bogue_report.py). Permet de
// télécharger le XLSX source, le DOCX généré, et de relancer la toolchain.

export default function VincentBatchesPage() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rerunningFor, setRerunningFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/vincent-batches`, { withCredentials: true });
      setBatches(data.batches || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de charger les lots Vincent.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadXlsx = (filename) => {
    window.open(`${API}/api/admin/vincent-batches/download-xlsx/${encodeURIComponent(filename)}`, "_blank");
  };
  const downloadDocx = (filename) => {
    window.open(`${API}/api/admin/vincent-batches/download-docx/${encodeURIComponent(filename)}`, "_blank");
  };

  const rerunToolchain = async (filename) => {
    setRerunningFor(filename);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/vincent-batches/rerun/${encodeURIComponent(filename)}`,
        {},
        { withCredentials: true, _allowRetry: true },
      );
      if (data.success) {
        toast.success(`Toolchain terminée — ${data.bogue_ids?.length || 0} bogues traités. DOCX : ${data.docx_name || "?"}`);
        await load();
      } else {
        const failed = data.steps?.find(s => s.returncode !== 0);
        toast.error(`Échec à l'étape « ${failed?.step || "?"} » : ${failed?.stderr || "inconnu"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'exécution de la toolchain.");
    } finally {
      setRerunningFor(null);
    }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / 1024 / 1024).toFixed(2)} Mo`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="vincent-batches-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#002FA7] dark:text-[#38BDF8] mb-2">
          Historique QA Vincent — Lots de consignes
        </h1>
        <p className="text-sm text-muted-foreground">
          Tous les fichiers XLSX de consignes envoyés par Vincent et traités par la toolchain
          (<code>parse_vincent_xlsx.py</code> → <code>sync_pending_from_changelog.py</code> → <code>gen_bogue_report.py</code>).
          Télécharge le XLSX source, le DOCX généré, ou relance la toolchain sur demande.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="vincent-batches-loading">
          <Clock size={32} className="mx-auto mb-2 animate-spin" />
          Chargement des lots…
        </div>
      ) : batches.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-muted/30 rounded-lg" data-testid="vincent-batches-empty">
          <Warning size={32} className="mx-auto mb-2 text-amber-500" />
          <p>Aucun XLSX Vincent trouvé.</p>
          <p className="text-xs mt-2">
            Dépose un fichier dans <code>memory/vincent_inputs/</code> puis rafraîchis.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {batches.map((b) => (
            <div
              key={b.xlsx_name}
              className="border border-border rounded-lg p-4 hover:border-[#002FA7]/40 transition-colors bg-card"
              data-testid={`batch-${b.xlsx_name}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <FileXls size={20} className="text-teal-600 flex-shrink-0" weight="fill" />
                    <h3 className="font-semibold text-sm truncate">{b.xlsx_name}</h3>
                  </div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                    <span>📅 {b.date}</span>
                    <span>📦 {fmtSize(b.size_bytes)}</span>
                    <span>🕐 {fmtDate(b.modified_at)}</span>
                    {b.bogue_count !== null && b.bogue_count !== undefined && (
                      <span className="font-semibold text-[#002FA7] dark:text-[#38BDF8]">
                        {b.bogue_count} bogue{b.bogue_count > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  {b.docx_name && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                      <FileDoc size={14} className="text-[#002FA7]" />
                      <span className="truncate">{b.docx_name}</span>
                      <span className="text-[10px]">({fmtSize(b.docx_size_bytes)})</span>
                      <CheckCircle size={12} className="text-teal-500 flex-shrink-0" weight="fill" />
                    </div>
                  )}
                  {!b.docx_name && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-amber-600">
                      <Warning size={14} weight="fill" />
                      <span>Aucun DOCX généré pour cette date</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => downloadXlsx(b.xlsx_name)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-teal-500/10 text-teal-700 dark:text-teal-400 hover:bg-teal-500/20 rounded-md border border-teal-500/20 transition-colors"
                    data-testid={`download-xlsx-${b.xlsx_name}`}
                    title="Télécharger le XLSX source"
                  >
                    <Download size={12} /> XLSX
                  </button>
                  {b.docx_name && (
                    <button
                      type="button"
                      onClick={() => downloadDocx(b.docx_name)}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[#002FA7]/10 text-[#002FA7] dark:text-[#38BDF8] hover:bg-[#002FA7]/20 rounded-md border border-[#002FA7]/20 transition-colors"
                      data-testid={`download-docx-${b.xlsx_name}`}
                      title="Télécharger le rapport DOCX"
                    >
                      <Download size={12} /> DOCX
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => rerunToolchain(b.xlsx_name)}
                    disabled={rerunningFor === b.xlsx_name}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20 rounded-md border border-amber-500/20 transition-colors disabled:opacity-50"
                    data-testid={`rerun-${b.xlsx_name}`}
                    title="Rejouer parse + sync + gen_bogue_report"
                  >
                    <ArrowClockwise size={12} className={rerunningFor === b.xlsx_name ? "animate-spin" : ""} />
                    {rerunningFor === b.xlsx_name ? "En cours…" : "Relancer toolchain"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 p-3 bg-muted/30 border border-border rounded-md text-xs text-muted-foreground">
        <strong>💡 Comment ajouter un nouveau lot ?</strong>
        <ul className="mt-2 list-disc pl-5 space-y-1">
          <li>Copie le XLSX de Vincent dans <code>/app/memory/vincent_inputs/</code></li>
          <li>Nomme-le avec la date : <code>YYYY-MM-DD_...xlsx</code></li>
          <li>Push sur GitHub → le workflow <code>vincent-batch-report.yml</code> se déclenche</li>
          <li>Ou clique « Relancer toolchain » ci-dessus pour l&apos;exécuter maintenant</li>
        </ul>
      </div>
    </div>
  );
}
